import { URLS as BASE_URLS } from '@shared/admin/routing';
import { build, buildFullPath } from '@shared/routing';
import type { UrlsType } from '@shared/types';

import type {
    AdminReporteeQuery,
    AdminStoryQuery,
    AdminUserReportsQuery,
    AgencyQuery,
    BillingFallbackSettingsQuery,
    BillingPaymentMethodsQuery,
    BillingReportQuery,
    ConversationQuery,
    DomainQuery,
    ExperimentsQuery,
    GetReferralDetailsQuery,
    GetReferralSummaryQuery,
    LogRecordQuery,
    MessageQuery,
    OfferProductQuery,
    OfferQuery,
    OfferRegionQuery,
    OperatorPaymentQuery,
    OperatorPaymentReportQuery,
    OperatorPayoutDetailsQuery,
    OperatorPayoutManageQuery,
    OperatorRefundQuery,
    PaymentMethodQuery,
    PaymentsGroupQuery,
    PaymentsQuery,
    RatingBonusQuery,
    RatingUserQuery,
    RefundsQuery,
    RoomSnapshotsAlertsQuery,
    RoomsQuery,
    SnapshotModerationQuery,
    StatsTableQuery,
    SubscriptionQuery,
    TimePackageQuery,
    UserAgeVerificationsQuery,
    UsersQuery,
    UserSubscriptionQuery,
    UserTimeChargesQuery,
    UserTimePackageQuery,
    UserVerificationsHistoryQuery,
    UserVerificationsQuery,
} from './types';

const urlList = {
    ...BASE_URLS,
    grayLog: (q: { key?: string; error_level?: string; roomId?: string; userId?: string }) => {
        const query: string[] = [];
        Object.entries(q).forEach(([key, value]) => {
            query.push(`${key}%3A${value}`);
        });
        return `https://gl.wbx.tools/streams/662bb34963866930a0428084/search?q=${
            query.join('+AND+')
        }&from=86400&rangetype=relative`;
    },
    users: (query?: UsersQuery) => build('/admin/users', query),
    userReports: (query: NonNullable<AdminUserReportsQuery>) => {
        return build(`/admin/users/[id]/reports`, query).replace('[id]', query.reporteeId as string);
    },
    logs: (query?: LogRecordQuery) => build('/admin/logs', query),
    domains: (query?: DomainQuery) => build('/admin/domains', query),
    permissions: (query?: UsersQuery) => build('/admin/permissions', query),
    permissionSettings: (query?: UsersQuery) => build('/admin/permissions/settings', query),
    userVerifications: (query?: UserVerificationsQuery) => build('/admin/users/verifications', query),
    userVerificationHistory: (query?: UserVerificationsHistoryQuery) => {
        return build('/admin/users/verifications/history', query);
    },
    userAgeVerification: (query?: UserAgeVerificationsQuery) => build('/admin/users/verifications/age', query),
    userTimeCharge: (query?: UserTimeChargesQuery) => build('/admin/users/time-charge', query),
    operatorPaymentList: (query?: OperatorPaymentQuery) => build('/admin/operator/payment', query),
    storiesSettings: () =>  build('/admin/story/settings'),
    stories: (query?: AdminStoryQuery) =>  build('/admin/story', query),
    operatorPaymentReportList: (query?: OperatorPaymentReportQuery) => build('/admin/operator/payments-report', query),
    operatorPayoutDetails: (query?: OperatorPayoutDetailsQuery) => build('/admin/operator/payout-details', query),
    operatorPayoutManage: (query?: OperatorPayoutManageQuery) => build('/admin/operator/payout-manage', query),
    userSnapshotModeration: (userId: string, query?: SnapshotModerationQuery) => {
        return build('/admin/users/[id]/moderation', query).replace('[id]', userId);
    },
    rooms: (query?: RoomsQuery) => build('/admin/rooms', query),
    conversations: (query?: ConversationQuery) => build('/admin/conversations', query),
    messages: (query?: MessageQuery) => build('/admin/messages', query),
    payments: (query?: PaymentsQuery, host?: string) => buildFullPath('/admin/payments', host, query),
    userTimePackages: (query?: UserTimePackageQuery) => build('/admin/users/time-package', query),
    userSubscriptions: (query?: UserSubscriptionQuery) => build('/admin/users/subscriptions', query),
    agency: (query?: AgencyQuery) => build('/admin/agency', query),
    refunds: (query?: RefundsQuery) => build('/admin/refunds', query),
    productTimePackages: (query?: TimePackageQuery) => build('/admin/product/time-package', query),
    productSubscriptions: (query?: SubscriptionQuery) => build('/admin/product/subscription', query),
    offerRegions: (query?: OfferRegionQuery) => build('/admin/product/offer-region', query),
    offerSales: (query?: OfferRegionQuery) => build('/admin/product/offer-sale', query),
    offers: (query?: OfferQuery) => build('/admin/product/offer', query),
    offerProducts: (query?: OfferProductQuery) => build('/admin/product/offer-product', query),
    paymentsGroups: (query?: PaymentsGroupQuery) => build('/admin/payments-groups', query),
    reports: (query: AdminReporteeQuery) => build('/admin/reports', query),
    ratings: (query: RatingUserQuery) => build('/admin/users/rating', query),
    paymentsBillingReport: (query?: BillingReportQuery) => build('/admin/utils/billing-report', query),
    adStats: (query?: StatsTableQuery) => build('/admin/stats/ad', query),
    alerts: (query?: RoomSnapshotsAlertsQuery) => build('/admin/rooms/alerts', query),
    referralsSummary: (query?: GetReferralSummaryQuery) => build('/admin/users/referrals', query),
    referralsDetails: (query?: GetReferralDetailsQuery) => build('/admin/users/referrals/details', query),
    operatorRefundList: (query?: OperatorRefundQuery) => build('/admin/operator/refund', query),
    experiments: (query?: ExperimentsQuery) => build('/admin/utils/experiments', query),
    contests: (query?: ExperimentsQuery) => build('/admin/contests', query),
    gifts: (query?: ExperimentsQuery) => build('/admin/product/gifts', query),
    billingPaymentMethods: (
        query?: BillingPaymentMethodsQuery
    ) => build('/admin/billing/billing-payment-methods', query),
    paymentMethods: (query?: PaymentMethodQuery) => build('/admin/billing/payment-method', query),
    billingFallbackSettings: (query?: BillingFallbackSettingsQuery) => build('/admin/billing/fallback-settings', query),
    ratingBonus: (query?: RatingBonusQuery) => build('/admin/operator/rating-bonus', query),
};

const URLS: UrlsType<typeof urlList> = urlList;

export { URLS };
